import React, { useEffect, useState } from "react";
import WebFont from "webfontloader";

const FONT_CACHE_KEY = "loadedGoogleFonts_v1";
const FONT_CACHE_TIMESTAMP_KEY = "fontCacheTimestamp_v1";
const CACHE_TTL_DAYS = 7;

const isCacheExpired = () => {
    const lastSaved = localStorage.getItem(FONT_CACHE_TIMESTAMP_KEY);
    if (!lastSaved) return true;
    const lastTime = parseInt(lastSaved, 10);
    const now = Date.now();
    return (now - lastTime) / (1000 * 60 * 60 * 24) > CACHE_TTL_DAYS;
};

const clearFontCache = () => {
    try {
        localStorage.removeItem(FONT_CACHE_KEY);
        localStorage.removeItem(FONT_CACHE_TIMESTAMP_KEY);    
    } catch (err) {
        console.warn("Failed to clear font cache:", err);
    }
};


export const fontList = [
    "Anton",
    "Arial",
    "Bebas Neue",
    "Book Antiqua",
    "Bookman Old Style",
    "Caveat",
    "Century Gothic",
    "Dancing Script",
    "Georgia",
    "Impact",
    "Indie Flower",
    "Lato",
    "Montserrat",
    "Open Sans",
    "Pacifico",
    "Playfair Display",
    "Roboto",
    "Times New Roman"
];

interface FontDropdownProps {
    selectedTextId: string | null;
    selectedFont: string;
    handleTextChange: (textId: string, updates: { fontFamily: string }) => void;
    preloadedFonts?: string[];
}

const localFonts = ["Arial",
    "Times New Roman",
    "Georgia",
    "Impact",
    "Book Antiqua",
    "Bookman Old Style",
    "Century Gothic",
    ];

const FontDropdown: React.FC<FontDropdownProps> = ({ selectedTextId, selectedFont, handleTextChange, preloadedFonts = fontList }) => {
    const [loadedFonts, setLoadedFonts] = useState<Record<string, boolean>>({});

    // Restore cached font info
    useEffect(() => {
        if (isCacheExpired()) clearFontCache();

        try {
            const cached = JSON.parse(localStorage.getItem(FONT_CACHE_KEY) || "{}");
            setLoadedFonts(cached);
        } catch {
            clearFontCache();
        }
    }, []);

    // Preload all fonts on mount to make previews instant
    useEffect(() => {
        const googleFonts = preloadedFonts.filter(f => !localFonts.includes(f));
        if (googleFonts.length === 0) return;

        // Load all Google fonts at once
        WebFont.load({
            google: { families: googleFonts.map(f => `${f}:400,700,400italic,700italic`) },
            fontactive: (familyName: string) => {
                const fam = familyName.replace(/:.*/, '');
                setLoadedFonts(prev => ({ ...prev, [fam]: true }));
            },
            active: () => {
                setLoadedFonts(prev => {
                    const merged = { ...prev };
                    [...localFonts, ...googleFonts].forEach(f => merged[f] = true);
                    try {
                        localStorage.setItem(FONT_CACHE_KEY, JSON.stringify(merged));
                        localStorage.setItem(FONT_CACHE_TIMESTAMP_KEY, Date.now().toString());
                    } catch { }
                    return merged;
                });
            },
            inactive: clearFontCache
        } as any);
    }, [preloadedFonts]);

    // Handle user selecting a font
    const handleChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        const font = e.target.value;
        if (!selectedTextId) return;

        if (!loadedFonts[font] && !localFonts.includes(font)) {
            WebFont.load({
                google: { families: [`${font}:400,700,400italic,700italic`] },
                active: () => {
                    setLoadedFonts(prev => {
                        const updated = { ...prev, [font]: true };
                        try {
                            localStorage.setItem(FONT_CACHE_KEY, JSON.stringify(updated));
                            localStorage.setItem(FONT_CACHE_TIMESTAMP_KEY, Date.now().toString());
                        } catch { }
                        return updated;
                    });
                    handleTextChange(selectedTextId, { fontFamily: font });
                    
                },
            } as any);
        } else {
            handleTextChange(selectedTextId, { fontFamily: font });
        }
    };


    return (
        <select
            className="font-family-dropdown"
            value={selectedFont || ""}
            onChange={handleChange}
            style={{
                height: 34,
                fontSize: 15,
                borderRadius: 6,
                padding: "4px 8px",
                background: "#fff",
                color: "#333",
                minWidth: 200
            }}
        >
            <option value="" disabled>Select font...</option>
            {fontList.map(font => (
                <option
                    key={font}
                    value={font}
                    style={{
                        fontFamily: loadedFonts[font] ? `"${font}", sans-serif` : "Arial, sans-serif",
                        fontSize: 16,
                        lineHeight: "22px",
                    }}
                >
                    {font} — AaBbCc
                </option>
            ))}
        </select>
    );
};

export default FontDropdown;
